import { useState, useEffect, useRef, useMemo } from 'react';
import Plotly from 'plotly.js-dist';
import Globe from 'globe.gl';
import { scaleLinear } from 'd3-scale';
import * as THREE from 'three';

// Debounce utility function with cancel method
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
};

// Helper function to get variable definition
const getVariableDefinition = (variable, definitions) => {
  if (!definitions || !variable) {
    return `${variable || 'Variable'} - Select a variable to see its definition`;
  }

  // Direct lookup first
  let definition = definitions[variable];

  if (!definition || definition.trim() === '' || definition === 'Definition not provided') {
    // Try case-insensitive and trimmed match
    const normalizedSelected = variable.trim().toLowerCase();
    const matchingKey = Object.keys(definitions).find(key => 
      key.trim().toLowerCase() === normalizedSelected
    );
    
    definition = matchingKey ? definitions[matchingKey] : null;
  }

  // Final fallback
  if (!definition || definition.trim() === '' || definition === 'Definition not provided') {
    definition = `${variable} - Definition not available`;
  }

  return definition;
};

// Initialize debounced function outside useEffect to prevent recreation
const debouncedSetHoverChartData = debounce((setHoverChartData, data) => {
  console.log('Debounced hover chart data set:', data);
  setHoverChartData(data);
}, 300);

// Helper function to determine the correct legend binary value
const getLegendBinary = (variable, measure, legendMap) => {
 // First try the exact key from legendMap (this is the most accurate)
 const mapKey = `${variable}-${measure}`;
 if (legendMap && legendMap[mapKey] !== undefined) {
   return legendMap[mapKey];
 }
 
 // ONLY for exact match "Total Population", force light green to dark green (binary = 2)
 // This ensures Population - Aged 65+ and Population - Aged 80+ use their correct Binary: 1
 if (variable === 'Population') {
   return 2;
 }
 
 // Default to higher is worse (green to red) if not specified
 return 0;
};

const MapView = ({ filteredData, countryMapping, m49Mapping, worldGeoJSON, selectedYear, setSelectedYear, mapMode, setMapMode, isSidebarOpen, isRightPanelOpen, dataStructure, selectedVariable, selectedMeasure, stopAnimation, legendBinary, legendMap = {}, variableDefinitions = {} }) => {
  const [mapError, setMapError] = useState(null);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(100);
  const [mapKey, setMapKey] = useState(0);
  const [mapDimensions, setMapDimensions] = useState({ width: 800, height: 500 });
  const [hoverChartData, setHoverChartData] = useState(null);
  const [hoverChartPosition, setHoverChartPosition] = useState({ x: 0, y: 0 });
  const [isAnimatingYear, setIsAnimatingYear] = useState(false);

  const mapContainerRef = useRef(null);
  const globeContainerRef = useRef(null);
  const plotlyMapRef = useRef(null);
  const hoverChartRef = useRef(null);
  const globeRef = useRef(null);
  const animationIntervalRef = useRef(null);
  const debugRef = useRef({
    mapLoaded: false,
    errors: []
  });
  const unhoverTimeoutRef = useRef(null);

  // Store event listener functions for cleanup
  const handleGlobeClickRef = useRef(null);
  const handleDragEndRef = useRef(null);
  const handlePointerUpRef = useRef(null);
  const handleResizeRef = useRef(null);

  // Font loading utility
  const ensureFontLoaded = () => {
    return new Promise((resolve) => {
      if (document.fonts && document.fonts.check) {
        if (document.fonts.check('300 16px Calibre-Light')) {
          resolve();
        } else {
          document.fonts.ready.then(() => {
            setTimeout(resolve, 100); // Small delay to ensure font is fully loaded
          });
        }
      } else {
        // Fallback for browsers without font loading API
        setTimeout(resolve, 500);
      }
    });
  };

  // Custom download function with watermarks for 2D map
  const downloadMapWithWatermark = async () => {
    if (!plotlyMapRef.current || !plotlyMapRef.current._fullLayout || mapMode !== '2d') return;
    
    try {
      // Ensure font is loaded before downloading
      await ensureFontLoaded();
      
      // Get current layout and data from the map
      const currentLayout = plotlyMapRef.current.layout;
      const currentData = plotlyMapRef.current.data;
      
      // Create layout with watermarks
      const watermarkedLayout = {
        ...currentLayout,
        images: [
          {
            source: '/logo.png',
            xref: 'paper',
            yref: 'paper',
            x: 0.98,
            y: 0.02,
            sizex: 0.12,
            sizey: 0.12,
            xanchor: 'right',
            yanchor: 'bottom',
            opacity: 0.9,
            layer: 'above'
          }
        ],
        annotations: [
          ...(currentLayout.annotations || []),
          {
            text: '© Health Systems Innovation Lab, Harvard University, 2025',
            xref: 'paper',
            yref: 'paper',
            x: 0.01,
            y: 0.01,
            xanchor: 'left',
            yanchor: 'bottom',
            showarrow: false,
            font: {
              size: 11,
              color: 'rgba(0, 0, 0, 0.9)',
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            borderwidth: 0,
            borderpad: 6
          }
        ],
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        }
      };
      
      // Temporarily update with watermarks
      await Plotly.react(plotlyMapRef.current, currentData, watermarkedLayout);
      
      // Download the image
      await Plotly.downloadImage(plotlyMapRef.current, {
        format: 'png',
        filename: `map_${selectedVariable || 'data'}_${selectedYear || 'all'}`,
        height: mapDimensions.height,
        width: mapDimensions.width,
        scale: 2
      });
      
      // Revert to original layout without watermarks
      await Plotly.react(plotlyMapRef.current, currentData, currentLayout);
      
    } catch (error) {
      console.error('Error downloading map with watermark:', error);
    }
  };

  // Custom download function for 3D globe (screenshot-based)
  const downloadGlobeWithWatermark = async () => {
    if (!globeRef.current || mapMode !== '3d') return;
    
    try {
      // Get the globe canvas
      const globeCanvas = globeRef.current.renderer().domElement;
      
      // Create a new canvas with watermarks
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = globeCanvas.width;
      finalCanvas.height = globeCanvas.height;
      const ctx = finalCanvas.getContext('2d');
      
      // Draw the globe screenshot
      ctx.drawImage(globeCanvas, 0, 0);
      
      // Add copyright text watermark
      ctx.font = '22px Calibre-Light, Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(10, finalCanvas.height - 40, 800, 30);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.fillText('© Health Systems Innovation Lab, Harvard University, 2025', 20, finalCanvas.height - 20);
      
      // Load and add logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        // Calculate logo position (bottom right)
        const logoWidth = 120;
        const logoHeight = 120;
        const logoX = finalCanvas.width - logoWidth - 20;
        const logoY = finalCanvas.height - logoHeight - 20;
        
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        
        // Download the final image
        finalCanvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `globe_${selectedVariable || 'data'}_${selectedYear || 'all'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });
      };
      logoImg.src = '/logo.png';
      
    } catch (error) {
      console.error('Error downloading globe with watermark:', error);
    }
  };

  // Memoize available years from filtered data
  const availableYears = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return dataStructure.years || [];
    }
    
    // Extract unique years from filtered data and sort them
    const yearsFromData = [...new Set(filteredData.map(row => row.Year))]
      .filter(year => year && Number.isInteger(year))
      .sort((a, b) => a - b);
    
    console.log('Available years from filtered data:', yearsFromData);
    return yearsFromData;
  }, [filteredData, dataStructure.years]);

  // Update selectedYear when availableYears change - ALWAYS set to first available year
  useEffect(() => {
    if (availableYears.length > 0) {
      // ALWAYS set to first available year when data changes (this fixes the middle position issue)
      const firstYear = availableYears[0];
      if (selectedYear !== firstYear) {
        setSelectedYear(firstYear);
        console.log(`Updated selectedYear from ${selectedYear} to ${firstYear} (first available year)`);
      }
    }
  }, [availableYears]); // Only depend on availableYears

  // Memoize countryData for Plotly choropleth map and 3D globe
  const countryData = useMemo(() => {
    if (!worldGeoJSON) {
      console.log('Cannot generate country data: worldGeoJSON is missing');
      return { locations: [], z: [], text: [], raw: {}, normalizedZ: [] };
    }

    const locations = [];
    const z = [];
    const normalizedZ = [];
    const text = [];
    const raw = {};
    let min = Infinity;
    let max = -Infinity;

    const yearData = filteredData.length > 0 && selectedYear ? filteredData.filter(row => row.Year === selectedYear) : [];
    console.log(`Data for year ${selectedYear}:`, yearData);

    const countriesByName = {};
    yearData.forEach(row => {
      if (row.Country && typeof row.Value === 'number' && !isNaN(row.Value)) {
        countriesByName[row.Country] = row.Value;
        if (row.Value < min) min = row.Value;
        if (row.Value > max) max = row.Value;
      }
    });

    const reversedM49 = Object.fromEntries(Object.entries(m49Mapping).map(([k, v]) => [v, k]));
    const nameVariations = {};
    const commonFixes = {
      "United States": ["USA", "United States of America", "US"],
      "United Kingdom": ["UK", "Great Britain", "England", "GBR"],
      "Russia": ["Russian Federation", "RUS"],
      "South Korea": ["Korea", "Korea, Republic of", "KOR"],
      "North Korea": ["Korea, Democratic People's Republic of", "PRK"],
      "Fiji": ["FJI"]
    };

    Object.entries(countriesByName).forEach(([countryName, value]) => {
      nameVariations[countryName] = value;
      if (countryMapping[countryName]) {
        nameVariations[countryMapping[countryName]] = value;
      }
      if (m49Mapping[countryName]) {
        nameVariations[m49Mapping[countryName]] = value;
        raw[m49Mapping[countryName]] = value;
      }
      Object.entries(commonFixes).forEach(([standard, variations]) => {
        if (countryName === standard) {
          variations.forEach(variant => {
            nameVariations[variant] = value;
          });
        }
        if (variations.includes(countryName)) {
          nameVariations[standard] = value;
          if (countryMapping[standard]) {
            nameVariations[countryMapping[standard]] = value;
          }
          if (m49Mapping[standard]) {
            nameVariations[m49Mapping[standard]] = value;
            raw[m49Mapping[standard]] = value;
          }
        }
      });
    });

    if (worldGeoJSON.features) {
      worldGeoJSON.features.forEach(feature => {
        let matched = false;
        let featureId = feature.id;
        const countryName = feature.properties?.name || reversedM49[featureId] || `Country ${featureId}`;
        let value;

        if (featureId && nameVariations[featureId] !== undefined) {
          value = nameVariations[featureId];
          matched = true;
        }

        if (!matched && featureId && reversedM49[featureId]) {
          const countryNameFromM49 = reversedM49[featureId];
          if (countryNameFromM49 && nameVariations[countryNameFromM49] !== undefined) {
            value = nameVariations[countryNameFromM49];
            matched = true;
          }
        }

        if (!matched && feature.properties) {
          const properties = feature.properties;
          const possibleMatches = [
            properties.ISO_A3,
            properties.iso_a3,
            properties.ISO_A2,
            properties.iso_a2,
            properties.ADM0_A3,
            properties.adm0_a3,
            properties.name,
            properties.NAME,
            properties.NAME_LONG,
            properties.name_long,
            properties.ADMIN,
            properties.admin,
            properties.FORMAL_EN,
            properties.formal_en
          ].filter(Boolean);

          for (const id of possibleMatches) {
            if (id && nameVariations[id] !== undefined) {
              value = nameVariations[id];
              matched = true;
              break;
            }
          }
        }

        if (matched && value !== undefined) {
          raw[featureId] = value;
          raw[countryName] = value;
          if (feature.properties?.ISO_A3) {
            raw[feature.properties.ISO_A3] = value;
            locations.push(feature.properties.ISO_A3);
            z.push(value);
            const normalizedValue = max !== min ? ((value - min) / (max - min)) * 100 : 50;
            normalizedZ.push(normalizedValue);
            text.push(`${countryName}: ${value.toFixed(2)} (${selectedYear || 'No year'})`);
          }
          console.log('Matched country:', { countryName, featureId, value, iso_a3: feature.properties?.ISO_A3 });
        } else {
          console.log('Unmatched country:', { countryName, featureId, iso_a3: feature.properties?.ISO_A3 });
        }
      });

      Object.entries(countriesByName).forEach(([countryName, value]) => {
        if (countryMapping[countryName] && !locations.includes(countryMapping[countryName])) {
          locations.push(countryMapping[countryName]);
          z.push(value);
          const normalizedValue = max !== min ? ((value - min) / (max - min)) * 100 : 50;
          normalizedZ.push(normalizedValue);
          text.push(`${countryName}: ${value.toFixed(2)} (${selectedYear || 'No year'})`);
          raw[countryMapping[countryName]] = value;
          raw[countryName] = value;
          console.log('Added country via countryMapping:', { countryName, iso_a3: countryMapping[countryName], value });
        }
      });
    }

    const computedMinValue = min !== Infinity ? min : 0;
    let computedMaxValue = max !== -Infinity ? max : 100;
    if (computedMinValue === computedMaxValue) {
      computedMaxValue = computedMinValue + 1;
    }
    setMinValue(computedMinValue);
    setMaxValue(computedMaxValue);

    console.log('Country data:', { locations, z, normalizedZ, text, raw, minValue: computedMinValue, maxValue: computedMaxValue });
    return { locations, z, normalizedZ, text, raw };
  }, [filteredData, countryMapping, m49Mapping, worldGeoJSON, selectedYear]);

  // Log filteredData for debugging
  useEffect(() => {
    console.log('MapView received filteredData:', filteredData);
    console.log('filteredData length:', filteredData.length);
    console.log('Sample data:', filteredData.slice(0, 5));
    console.log('Map mode:', mapMode);
    console.log('Legend binary:', legendBinary);
    console.log('Selected variable:', selectedVariable);
    console.log('Legend map:', legendMap);
  }, [filteredData, mapMode, legendBinary, selectedVariable, legendMap]);

  // Helper function to get country name from feature (shared between 2D and 3D)
  const getCountryNameFromFeature = (feature) => {
    const featureId = feature.id ? String(feature.id) : null;
    const reversedM49 = Object.fromEntries(Object.entries(m49Mapping).map(([k, v]) => [v, k]));
    
    // Try various sources for country name
    const possibleNames = [
      feature.properties?.name,
      feature.properties?.NAME,
      feature.properties?.NAME_LONG,
      feature.properties?.name_long,
      feature.properties?.ADMIN,
      feature.properties?.admin,
      feature.properties?.FORMAL_EN,
      feature.properties?.formal_en,
      featureId && reversedM49[featureId]
    ].filter(Boolean);

    // Return the first valid name found
    for (const name of possibleNames) {
      // Check if we have data for this country name
      const hasData = filteredData.some(row => row.Country === name);
      if (hasData) return name;
    }

    // Fallback: return the first available name or a default
    return possibleNames[0] || `Country ${featureId || 'unknown'}`;
  };

  // Helper function to show hover chart (shared between 2D and 3D)
  const showHoverChart = (countryName, mouseX, mouseY) => {
    console.log('Showing hover chart for:', countryName);
    
    const countryData = filteredData.filter(row => row.Country === countryName);
    if (countryData.length > 0 && selectedVariable && selectedMeasure) {
      const years = availableYears; // Use availableYears instead of dataStructure.years
      const values = years.map(year => {
        const row = countryData.find(r => r.Year === year);
        return row && typeof row.Value === 'number' && !isNaN(row.Value) ? row.Value : null;
      });

      // Calculate chart position relative to the map container
      const mapRect = mapContainerRef.current.getBoundingClientRect();
      const chartWidth = 300;
      const chartHeight = 200;
      
      // Position chart to the left and above the cursor/hover point
      let chartX = mouseX - chartWidth - 100;
      let chartY = mouseY - chartHeight - 50;

      // Ensure chart stays within viewport and map container
      chartX = Math.max(mapRect.left, Math.min(chartX, mapRect.right - chartWidth - 20));
      chartY = Math.max(mapRect.top, Math.min(chartY, mapRect.bottom - chartHeight - 20));

      // Clear any pending unhover timeout
      if (unhoverTimeoutRef.current) {
        clearTimeout(unhoverTimeoutRef.current);
      }

      debouncedSetHoverChartData(setHoverChartData, {
        country: countryName,
        years,
        values,
        x: chartX,
        y: chartY
      });
    } else {
      debouncedSetHoverChartData(setHoverChartData, null);
      console.log('No country data found or missing selections for:', countryName, { selectedVariable, selectedMeasure });
    }
  };

  // Helper function to hide hover chart (shared between 2D and 3D)
  const hideHoverChart = () => {
    console.log('Hiding hover chart');
    debouncedSetHoverChartData.cancel();
    unhoverTimeoutRef.current = setTimeout(() => {
      setHoverChartData(null);
      console.log('Hover chart cleared after unhover delay');
    }, 500);
  };

  // Handle resize for window and sidebar/panel changes
  useEffect(() => {
    const handleResize = () => {
      if (mapContainerRef.current) {
        const containerRect = mapContainerRef.current.getBoundingClientRect();
        const containerWidth = Math.max(containerRect.width - 40, 400);
        const containerHeight = Math.max(containerRect.height - 80, 300); // Space for year slider

        setMapDimensions({
          width: containerWidth,
          height: containerHeight
        });

        // Only use simple resize for immediate response
        if (mapMode === '2d' && plotlyMapRef.current && window.Plotly) {
          // Check if plot is initialized before resizing
          const plotElement = plotlyMapRef.current;
          if (plotElement._fullLayout && plotElement.data) {
            setTimeout(() => {
              try {
                Plotly.Plots.resize(plotElement);
                console.log('Plotly resized due to panel change');
              } catch (error) {
                console.warn('Resize failed:', error);
              }
            }, 150);
          }
        }

        if (mapMode === '3d' && globeRef.current) {
          globeRef.current.width(containerWidth).height(containerHeight);
        }

        console.log('Resizing map to:', { 
          width: containerWidth, 
          height: containerHeight, 
          containerRect,
          mapMode
        });
      }
    };

    const debouncedResize = debounce(handleResize, 100);
    setTimeout(handleResize, 100);
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, [isSidebarOpen, isRightPanelOpen, mapMode]);

  // Re-render map when dimensions change (for panel opening/closing)
  useEffect(() => {
    if (mapMode === '2d' && plotlyMapRef.current && window.Plotly && mapDimensions.width > 0) {
      // Check if the plot is properly initialized
      const plotElement = plotlyMapRef.current;
      if (!plotElement._fullLayout || !plotElement.data) {
        console.log('Plot not fully initialized, skipping relayout');
        return;
      }

      console.log('Updating map layout due to dimension change:', mapDimensions);
      
      const update = {
        width: mapDimensions.width,
        height: mapDimensions.height
      };

      // Use a timeout to ensure the DOM has settled
      setTimeout(() => {
        if (plotElement._fullLayout && plotElement.data) {
          Plotly.relayout(plotElement, update).then(() => {
            console.log('Map layout updated successfully');
          }).catch(error => {
            console.warn('Error updating map layout, falling back to resize:', error);
            // Fallback to resize if relayout fails
            try {
              Plotly.Plots.resize(plotElement);
            } catch (resizeError) {
              console.error('Resize also failed:', resizeError);
            }
          });
        }
      }, 200);
    }
  }, [mapDimensions, mapMode]);

  // Force re-render only when switching to 2D mode
  useEffect(() => {
    if (mapMode === '2d') {
      setMapKey(prevKey => prevKey + 1);
      console.log('Switched to 2D mode, forcing re-render with mapKey:', mapKey + 1);
    }
  }, [mapMode]);

  // Check worldGeoJSON
  useEffect(() => {
    if (worldGeoJSON) {
      debugRef.current.mapLoaded = true;
      console.log('World map loaded with', worldGeoJSON.features.length, 'features');
    } else {
      console.warn('No worldGeoJSON provided to MapView');
      setMapError('No map data available');
    }
  }, [worldGeoJSON]);

  // Render Plotly 2D choropleth map with hover line chart
  useEffect(() => {
    if (mapMode !== '2d' || !worldGeoJSON || !plotlyMapRef.current) return;

    const mapRef = plotlyMapRef.current;

    // Determine colorscale based on legendBinary using helper function
    const currentLegendBinary = getLegendBinary(selectedVariable, selectedMeasure, legendMap);
    
    let colorscale;
    if (currentLegendBinary === 2) {
      // Light green to dark green gradient
      colorscale = [
        [0, '#c6f6d5'], // Very light green
        [0.25, '#9ae6b4'], // Light green
        [0.5, '#68d391'], // Medium green
        [0.75, '#38a169'], // Dark green
        [1, '#2f855a'] // Very dark green
      ];
    } else if (currentLegendBinary === 0) {
      // Lower is better: Green (good) to Red (bad)
      colorscale = [
        [0, '#2cba00'], // Green for low values (better)
        [0.25, '#a3ff00'],
        [0.5, '#fff400'],
        [0.75, '#ffa700'],
        [1, '#ff0000'] // Red for high values (worse)
      ];
    } else {
      // Higher is better: Red (bad) to Green (good)
      colorscale = [
        [0, '#ff0000'], // Red for low values (worse)
        [0.25, '#ffa700'],
        [0.5, '#fff400'],
        [0.75, '#a3ff00'],
        [1, '#2cba00'] // Green for high values (better)
      ];
    }

    const data = [
      {
        type: 'choropleth',
        locations: countryData.locations.length > 0 ? countryData.locations : [],
        z: countryData.normalizedZ.length > 0 ? countryData.normalizedZ : [],
        text: countryData.text.length > 0 ? countryData.text : [],
        colorscale,
        zmin: 0,
        zmax: 100,
        marker: {
          line: {
            color: '#D6D6DA',
            width: 0.5
          }
        },
        colorbar: {
          title: currentLegendBinary === 2 ? 'Population Size (Low to High)' : 'Normalized Scale (Low to High)',
          titleside: 'top',
          x: 0.5,
          y: -0.20, // Position below the map, above the year slider
          xanchor: 'center',
          yanchor: 'top',
          len: 0.6, // Make it shorter horizontally
          thickness: 12,
          orientation: 'h', // Horizontal orientation
          tickvals: [],
          ticktext: [],
          tickfont: {
            size: 10,
            family: 'Arial, sans-serif'
          }
        },
        hoverinfo: 'text'
      }
    ];

    const layout = {
      geo: {
        scope: 'world',
        projection: {
          type: 'natural earth'
        },
        showframe: false,
        showcoastlines: true,
        coastlinecolor: '#D6D6DA',
        showland: true,
        landcolor: '#F5F5F5',
        showocean: true,
        oceancolor: '#1e3a5f',
        showrivers: false,
        showlakes: true,
        lakecolor: '#2563eb',
        showcountries: true,
        countrycolor: '#D6D6DA'
      },
      width: mapDimensions.width,
      height: mapDimensions.height,
      margin: {
        l: 40,
        r: 40,
        t: 40,
        b: 120
      },
      annotations: [],
      paper_bgcolor: 'white',
      plot_bgcolor: 'white'
    };

    const config = {
      responsive: true,
      displayModeBar: false, // Hide default mode bar since we're using custom download
      displaylogo: false,
      toImageButtonOptions: {
        format: 'png',
        filename: `map_${selectedYear || 'data'}`,
        height: mapDimensions.height,
        width: mapDimensions.width,
        scale: 2
      }
    };

    try {
      Plotly.newPlot(mapRef, data, layout, config);
      console.log('Plotly 2D choropleth map rendered successfully', { locationsLength: countryData.locations.length });

      // Handle hover to show line chart
      mapRef.on('plotly_hover', (eventData) => {
        console.log('plotly_hover triggered', {
          eventData,
          point: eventData.points && eventData.points[0] ? eventData.points[0] : null
        });
        if (eventData.points && eventData.points[0]) {
          const point = eventData.points[0];
          const countryName = point.text.split(':')[0].trim();
          
          const mapRect = mapRef.getBoundingClientRect();
          
          let xPixel, yPixel;
          if (point.lon !== undefined && point.lat !== undefined) {
            const geo = mapRef._fullLayout.geo;
            const projection = geo._subplot.projection;
            const [px, py] = projection([point.lon, point.lat]);
            xPixel = mapRect.left + (px * mapRect.width);
            yPixel = mapRect.top + (py * mapRect.height);
          } else {
            xPixel = eventData.event.clientX;
            yPixel = eventData.event.clientY;
          }

          showHoverChart(countryName, xPixel, yPixel);
        }
      });

      // Clear hover chart on unhover with delay
      mapRef.on('plotly_unhover', () => {
        console.log('plotly_unhover triggered');
        hideHoverChart();
      });
    } catch (error) {
      console.error('Error rendering Plotly map:', error);
      setMapError('Failed to render map: ' + error.message);
      debugRef.current.errors.push({
        type: 'plotly',
        message: error.message,
        timestamp: new Date().toISOString()
      });
    }

    return () => {
      if (mapRef) {
        Plotly.purge(mapRef);
      }
      debouncedSetHoverChartData.cancel();
      if (unhoverTimeoutRef.current) {
        clearTimeout(unhoverTimeoutRef.current);
      }
    };
  }, [mapMode, worldGeoJSON, countryData, mapDimensions, minValue, maxValue, mapKey, filteredData, selectedVariable, selectedMeasure, selectedYear, legendMap]);

  // Render hover line chart
  useEffect(() => {
    if (!hoverChartData || !hoverChartRef.current || !selectedVariable || !selectedMeasure) {
      if (hoverChartRef.current) {
        Plotly.purge(hoverChartRef.current);
      }
      return;
    }

    const chartRef = hoverChartRef.current;

    const { country, years, values } = hoverChartData;
    const validData = years.reduce((acc, year, i) => {
      if (values[i] !== null) {
        acc.years.push(year);
        acc.values.push(values[i]);
      }
      return acc;
    }, { years: [], values: [] });

    console.log('Hover chart data prepared:', { country, validYears: validData.years, validValues: validData.values });

    if (validData.years.length === 0) {
      setHoverChartData(null);
      console.log('No valid data for hover chart:', country);
      return;
    }

    // Find the value for the selected year
    const selectedYearIndex = validData.years.indexOf(selectedYear);
    const selectedYearValue = selectedYearIndex !== -1 && validData.values[selectedYearIndex] !== null 
      ? validData.values[selectedYearIndex].toFixed(2) 
      : 'N/A';

    const data = [
      {
        x: validData.years,
        y: validData.values,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#1f77b4' },
        marker: { size: 8 },
        name: country
      },
      {
        type: 'scatter',
        mode: 'lines',
        x: [selectedYear, selectedYear],
        y: [Math.min(...validData.values.filter(v => v !== null)), Math.max(...validData.values.filter(v => v !== null))],
        line: {
          color: 'red',
          dash: 'dash',
          width: 2
        },
        showlegend: false,
        hoverinfo: 'none'
      }
    ];

    const layout = {
      title: {
        text: `${country}: ${selectedYearValue} (${selectedYear || 'No year'})`,
        font: { size: 14, family: 'Arial, sans-serif' },
        x: 0.5,
        xanchor: 'center',
        y: 0.95,
        yanchor: 'top'
      },
      xaxis: {
        tickfont: { size: 10, family: 'Arial, sans-serif' },
        range: [Math.min(...validData.years), Math.max(...validData.years)]
      },
      yaxis: {
        tickfont: { size: 10, family: 'Arial, sans-serif' }
      },
      width: 300,
      height: 200,
      margin: { l: 60, r: 20, t: 40, b: 50 },
      paper_bgcolor: '#ffffff',
      plot_bgcolor: '#ffffff',
      showlegend: false,
      annotations: []
    };

    const config = {
      displayModeBar: false,
      staticPlot: false
    };

    try {
      if (chartRef && document.body.contains(chartRef)) {
        Plotly.newPlot(chartRef, data, layout, config);
        console.log('Hover line chart rendered for:', country);
        setHoverChartPosition({
          x: hoverChartData.x,
          y: hoverChartData.y
        });
      } else {
        console.error('Hover chart ref is invalid or not in DOM:', chartRef);
      }
    } catch (error) {
      console.error('Error rendering hover line chart:', error);
      debugRef.current.errors.push({
        type: 'hover_chart',
        message: error.message,
        timestamp: new Date().toISOString()
      });
    }

    return () => {
      if (chartRef && document.body.contains(chartRef)) {
        Plotly.purge(chartRef);
      }
    };
  }, [hoverChartData, selectedVariable, selectedMeasure, selectedYear]);

  // Initialize 3D globe ONLY once when switching to 3D mode
  useEffect(() => {
    if (mapMode !== '3d' || !worldGeoJSON || !globeContainerRef.current) return;

    if (globeRef.current) {
      console.log('Globe already exists, skipping initialization');
      return;
    }

    const globeContainer = globeContainerRef.current;
    console.log('Initializing new 3D globe');

    const globe = Globe()(globeContainer)
      .globeImageUrl(null)
      .backgroundColor('rgba(255, 255, 255, 0)')
      .showGlobe(true)
      .globeMaterial(new THREE.MeshLambertMaterial({ color: '#1e3a5f', transparent: true, opacity: 0.9 }))
      .polygonsData(worldGeoJSON.features)
      .polygonCapColor(() => '#F5F5F5')
      .polygonSideColor(() => '#D6D6DA')
      .polygonStrokeColor(() => '#D6D6DA')
      .polygonLabel(() => '')
      .onPolygonHover((hoverD, prevHoverD) => {
        if (hoverD) {
          globe.polygonAltitude(d => d === hoverD ? 0.1 : 0.05);
          
          if (selectedVariable && selectedMeasure) {
            const countryName = getCountryNameFromFeature(hoverD);
            
            const globeRect = globeContainer.getBoundingClientRect();
            const centerX = globeRect.left + globeRect.width / 2;
            const centerY = globeRect.top + globeRect.height / 2;
            
            const chartX = centerX + 200;
            const chartY = centerY - 100;
            
            console.log('3D Globe hover on:', countryName);
            showHoverChart(countryName, chartX, chartY);
          }
        } else {
          globe.polygonAltitude(0.05);
          hideHoverChart();
        }
      });

    globeRef.current = globe;

    const controls = globe.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1;

    handleGlobeClickRef.current = () => {
      console.log('Globe clicked');
    };

    handleDragEndRef.current = () => {
      console.log('Globe drag ended');
    };

    handlePointerUpRef.current = () => {
      console.log('Pointer up on globe');
    };

    handleResizeRef.current = () => {
      if (globeContainer && mapContainerRef.current) {
        const containerRect = mapContainerRef.current.getBoundingClientRect();
        globe.width(containerRect.width).height(containerRect.height - 60);
      }
    };

    globe.onGlobeClick(handleGlobeClickRef.current);
    controls.addEventListener('end', handleDragEndRef.current);
    globeContainer.addEventListener('pointerup', handlePointerUpRef.current);
    window.addEventListener('resize', handleResizeRef.current);

    handleResizeRef.current();

    return () => {
      console.log('Cleaning up globe initialization');
      if (globe) {
        globe.onGlobeClick(null);
        globe.onPolygonHover(null);
      }
      if (controls) {
        controls.removeEventListener('end', handleDragEndRef.current);
      }
      if (globeContainer) {
        globeContainer.removeEventListener('pointerup', handlePointerUpRef.current);
      }
      window.removeEventListener('resize', handleResizeRef.current);
      
      debouncedSetHoverChartData.cancel();
      if (unhoverTimeoutRef.current) {
        clearTimeout(unhoverTimeoutRef.current);
      }
      
      globeRef.current = null;
    };
  }, [mapMode, worldGeoJSON, mapDimensions, isSidebarOpen, isRightPanelOpen]);

  // Update globe colors and labels when data changes (separate from initialization)
  useEffect(() => {
    if (!globeRef.current || mapMode !== '3d' || !worldGeoJSON || !countryData) {
      return;
    }

    console.log('Updating globe colors and labels for year:', selectedYear, { minValue, maxValue });
    
    // Use different color scales based on legendBinary for 3D globe using helper function
    const currentLegendBinary = getLegendBinary(selectedVariable, selectedMeasure, legendMap);
    
    let colorScale;
    if (currentLegendBinary === 2) {
      // Light green to dark green gradient for 3D globe
      colorScale = scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#c6f6d5', '#9ae6b4', '#68d391', '#38a169', '#2f855a']);
    } else if (currentLegendBinary === 0) {
      // Lower is better: Green (good) to Red (bad)
      colorScale = scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#2cba00', '#a3ff00', '#fff400', '#ffa700', '#ff0000']);
    } else {
      // Higher is better: Red (bad) to Green (good)
      colorScale = scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#ff0000', '#ffa700', '#fff400', '#a3ff00', '#2cba00']);
    }

    const getColorForFeature = (feature) => {
      const featureId = feature.id ? String(feature.id) : null;
      let value = featureId ? countryData.raw[featureId] : undefined;
      
      if (value === undefined && feature.properties) {
        const props = feature.properties;
        const possibleIds = [
          props.ISO_A3,
          props.iso_a3,
          props.ISO_A2,
          props.iso_a2,
          props.ADM0_A3,
          props.adm0_a3,
          props.name,
          props.NAME,
          props.NAME_LONG,
          props.name_long,
          props.ADMIN,
          props.admin,
          props.FORMAL_EN,
          props.formal_en
        ].filter(Boolean);
        
        for (const id of possibleIds) {
          if (countryData.raw[id] !== undefined) {
            value = countryData.raw[id];
            break;
          }
        }
      }
      
      let normalizedValue;
      if (value !== undefined && maxValue !== minValue) {
        normalizedValue = ((value - minValue) / (maxValue - minValue)) * 100;
      } else {
        normalizedValue = value !== undefined ? 50 : undefined;
      }
      
      return normalizedValue !== undefined ? colorScale(normalizedValue) : '#F5F5F5';
    };

    const getLabelForFeature = (feature) => {
      const featureId = feature.id ? String(feature.id) : null;
      const reversedM49 = Object.fromEntries(Object.entries(m49Mapping).map(([k, v]) => [v, k]));
      const countryName = feature.properties?.name || (featureId && reversedM49[featureId]) || `Country ${featureId || 'unknown'}`;
      
      let value = featureId ? countryData.raw[featureId] : undefined;
      if (value === undefined && feature.properties) {
        const props = feature.properties;
        const possibleIds = [
          props.ISO_A3, props.iso_a3, props.ISO_A2, props.iso_a2,
          props.ADM0_A3, props.adm0_a3, props.name, props.NAME,
          props.NAME_LONG, props.name_long, props.ADMIN, props.admin,
          props.FORMAL_EN, props.formal_en
        ].filter(Boolean);
        
        for (const id of possibleIds) {
          if (countryData.raw[id] !== undefined) {
            value = countryData.raw[id];
            break;
          }
        }
      }
      
      const displayValue = typeof value === 'number' && !isNaN(value) 
        ? value.toFixed(2)
        : 'No data';
        
      return `
        <div class="map-tooltip">
          ${countryName}: ${displayValue} (${selectedYear || 'No year'})
        </div>
      `;
    };

    globeRef.current
      .polygonCapColor(getColorForFeature)
      .polygonLabel(getLabelForFeature);

    console.log('Globe colors and labels updated successfully');
  }, [countryData, selectedYear, minValue, maxValue, m49Mapping, mapMode, worldGeoJSON, selectedVariable, selectedMeasure, legendMap]);

  return (
    <div 
      className="map-view-container" 
      ref={mapContainerRef} 
      style={{ 
        flex: 1, 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column'
      }}
      data-mode={mapMode}
    >
      <div className="map-visualization-container" style={{ 
        width: '100%', 
        flex: 1,
        display: 'flex', 
        position: 'relative',
        minHeight: '340px'
      }}>
        {mapError ? (
          <div className="map-error">
            <p>Error loading map: {mapError}</p>
            <button 
              className="button button-secondary"
              onClick={() => console.log("Map debug info:", debugRef.current)}
            >
              Debug in Console
            </button>
          </div>
        ) : worldGeoJSON ? (
          <>
            {mapMode === '2d' && (
              <div 
                ref={plotlyMapRef} 
                className="plotly-map"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '340px'
                }}
              >
                {countryData.locations.length === 0 && (
                  <div className="map-data-status">
                    <p>No country data available for year {selectedYear || 'N/A'}</p>
                    <p>Try different filters</p>
                  </div>
                )}
              </div>
            )}
            {mapMode === '3d' && (
              <div 
                ref={globeContainerRef} 
                className="globe-container"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '340px'
                }}
              />
            )}
            
            {/* Custom download button for both 2D and 3D modes */}
            {selectedVariable && selectedMeasure && (
              <button
                onClick={mapMode === '2d' ? downloadMapWithWatermark : downloadGlobeWithWatermark}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#374151',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: 'Calibre-Light, Arial, sans-serif',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(249, 250, 251, 1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                }}
                title={`Download ${mapMode === '2d' ? 'map' : 'globe'} as PNG with watermark`}
              >
                <i className="fas fa-download" style={{ fontSize: '10px' }}></i>
                PNG
              </button>
            )}
            
            {hoverChartData && (
              <div
                ref={hoverChartRef}
                className="hover-line-chart"
                style={{
                  position: 'absolute',
                  left: `${hoverChartPosition.x}px`,
                  top: `${hoverChartPosition.y}px`,
                  width: '300px',
                  height: '200px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #ccc',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  borderRadius: '4px',
                  padding: '5px',
                  pointerEvents: 'auto' // Enable pointer events for interactivity
                }}
              >
                <button
                  onClick={() => {
                    console.log('Close button clicked');
                    hideHoverChart();
                  }}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    width: '24px',
                    height: '24px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001
                  }}
                  title="Close chart"
                >
                  ✕
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="map-placeholder">
            <p>World Map Visualization</p>
            <p>(Map loading...)</p>
          </div>
        )}
      </div>
      
      <div className="year-slider-container" style={{ 
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid #e0e0e0',
        borderRadius: '20px',
        padding: '8px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        width: 'auto',
        minWidth: '300px',
        maxWidth: '400px'
      }}>
        <button
          className="play-pause-button"
          onClick={() => {
            if (isAnimatingYear) {
              stopAnimation();
              setIsAnimatingYear(false);
              clearInterval(animationIntervalRef.current);
              animationIntervalRef.current = null;
            } else {
              if (!availableYears.length) return;
              setIsAnimatingYear(true);
              let index = availableYears.indexOf(selectedYear) + 1;
              if (index >= availableYears.length) index = 0;
              animationIntervalRef.current = setInterval(() => {
                if (index >= availableYears.length) index = 0;
                setSelectedYear(availableYears[index]);
                index++;
              }, 1000);
            }
          }}
          disabled={!selectedMeasure || availableYears.length < 2}
          title={isAnimatingYear ? 'Pause' : 'Play'}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '10px',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isAnimatingYear ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          min={availableYears.length > 0 ? availableYears[0] : 2010}
          max={availableYears.length > 0 ? availableYears[availableYears.length - 1] : 2030}
          step="1"
          value={availableYears.length > 0 && availableYears.includes(selectedYear) ? selectedYear : (availableYears[0] || 2010)}
          onChange={e => {
            const newYear = Number(e.target.value);
            // Only allow years that are in availableYears
            if (availableYears.length > 0 && availableYears.includes(newYear)) {
              setSelectedYear(newYear);
            } else if (availableYears.length > 0) {
              // Find closest available year
              const closest = availableYears.reduce((prev, curr) => 
                Math.abs(curr - newYear) < Math.abs(prev - newYear) ? curr : prev
              );
              setSelectedYear(closest);
            }
            
            if (isAnimatingYear) {
              stopAnimation();
              setIsAnimatingYear(false);
              clearInterval(animationIntervalRef.current);
              animationIntervalRef.current = null;
            }
          }}
          disabled={!selectedMeasure || availableYears.length === 0}
          className="year-slider"
          style={{ 
            width: '180px', 
            margin: '0 8px',
            height: '4px',
            cursor: 'pointer'
          }}
        />
        <div className="year-label" style={{
          minWidth: '40px',
          textAlign: 'center',
          fontSize: '12px',
          fontWeight: '600',
          color: '#333'
        }}>
          {availableYears.length > 0 && availableYears.includes(selectedYear) ? selectedYear : (availableYears[0] || 'N/A')}
        </div>
        <button
          onClick={() => setMapMode(mapMode === '2d' ? '3d' : '2d')}
          style={{
            marginLeft: '12px',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            minWidth: '32px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',  
            justifyContent: 'center'
          }}
          title={mapMode === '2d' ? 'Switch to 3D Globe' : 'Switch to 2D Map'}
        >
          {mapMode === '2d' ? '3D' : '2D'}
        </button>
      </div>
    </div>
  );
};

export default MapView;